import { useState } from 'react';
import { useFocusRing, mergeProps } from 'react-aria';

import { RouteManager } from '../../RouteManager';
import { ToastManager } from '../../ToastManager';

import { useI18n } from '../../hooks/useI18nFormatters';

import { glyph } from '../../theme/glyph';
import { theme } from '../../theme/theme';

import { ContextMenu } from '../../components/common/context-menu/ContextMenu';
import { ContextMenuButton } from '../../components/common/context-menu/ContextMenuButton';
import { useOptionContext } from '../../components/list-box/ListBox';
import { promptMessage } from '../../components/overlay/DialogPrompt';
import { SubNavItem } from '../../components/sub-navigation/SubNavItem';

import { ScriptNavigationItemContextMenuContent } from './ScriptNavigationItemContextMenuContent';
import { scriptsStore } from './ScriptsStore';

export function ScriptNavigationItem(props) {
  const { i18n } = useI18n();

  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  const { ref, option, state, item, value } = useOptionContext();
  const focusRing = useFocusRing();

  const [isRenaming, setIsRenaming] = useState(false);

  function renderRenaming() {
    return (
      <SubNavItem.NameTextField
        aria-label={i18n.messageFormatter('script.nameLabel')}
        defaultValue={item.textValue}
        selectDefaultValue={true}
        onSaveRequest={(value) => {
          scriptsStore
            .updateScript({
              id: item.key,
              script: { name: value },
            })
            .then((script) => {
              ToastManager.add({
                icon: glyph.core.paper,
                i18n: {
                  key: 'script.savedMessage',
                  props: { name: script.name },
                },
              });
            })
            .catch(ToastManager.handleError);

          setIsRenaming(false);
        }}
        onCancelRequest={() => {
          setIsRenaming(false);
        }}
      />
    );
  }

  const contextMenuContent = (
    <ScriptNavigationItemContextMenuContent
      scriptId={item.key}
      onRenameRequest={() => {
        // This is not implemented for now. Needs support in the HomeyScript app.
        setIsRenaming(true);
      }}
      onStartRequest={() => {
        Promise.resolve()
          .then(async () => {
            await scriptsStore.testScript({ id: item.key });
          })
          .catch(ToastManager.handleError);
      }}
      onDeleteRequest={() => {
        Promise.resolve()
          .then(async () => {
            try {
              await promptMessage({
                message: i18n.messageFormatter('prompt.delete', {
                  name: item.textValue,
                }),
              });

              await scriptsStore.deleteScript({ id: item.key });

              ToastManager.add({
                icon: glyph.core.paper,
                i18n: {
                  key: 'script.deletedMessage',
                  props: { name: item.textValue },
                },
              });
            } catch {
              // canceled
            }
          })
          .catch(ToastManager.handleError);
      }}
    />
  );

  return (
    <ContextMenu content={contextMenuContent}>
      {({ isOpen, onContextMenu }) => {
        return (
          <SubNavItem.Root
            {...mergeProps(option.optionProps, focusRing.focusProps)}
            ref={ref}
            data-is-selected={option.isSelected}
            data-is-renaming={isRenaming}
            data-is-focus-visible={focusRing.isFocusVisible}
            data-is-context-menu-open={isOpen}
            onContextMenu={onContextMenu}
          >
            <SubNavItem.Link isDisabled={isRenaming} href={RouteManager.getPathForScript(item.key)}>
              {(() => {
                if (item.value.context.script.version === 1) {
                  return (
                    <div style={{ position: 'relative' }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          zIndex: 1,
                          position: 'absolute',
                          right: -6,
                          bottom: -1,
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: theme.color.yellow,
                          border: `2px solid ${theme.color.component}`,
                        }}
                      />

                      <SubNavItem.Icon source={glyph.core.paper} size={theme.icon.size_default} />
                    </div>
                  );
                }

                return <SubNavItem.Icon source={glyph.core.paper} size={theme.icon.size_default} />;
              })()}

              {/* <SubNavItem.Icon source={glyph.core.paper} size={theme.icon.size_default} /> */}

              {isRenaming ? (
                renderRenaming()
              ) : (
                <SubNavItem.Name>
                  {item.textValue}
                  <SubNavItem.Extension>.js</SubNavItem.Extension>
                </SubNavItem.Name>
              )}
            </SubNavItem.Link>

            {isRenaming === false && (
              <ContextMenuButton
                className={SubNavItem.contextMenuButtonClass}
                isActive={option.isSelected}
                isOpen={isOpen}
                onPress={onContextMenu}
              />
            )}
          </SubNavItem.Root>
        );
      }}
    </ContextMenu>
  );
}
